import {useTranslation} from 'react-i18next';

import {Grid, MenuItem, TextField, useTheme} from '@mui/material';

import {InfoBanner} from '../../../../../components/InfoBanner/InfoBanner';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import {SENIORITY_LEVELS, SeniorityLevel} from '../../../../../lib/constants';
import {SupportedLanguages} from '../../../../../types/SupportedLanguages';
import {Department} from '../../../profile/types';
import {DepartmentTargetProfileAssignment} from '../../types/DepartmentTargetProfileTypes';
import type {WorkforceProfileResearchLookup, WorkforceProfileWizardSetup} from '../../types/workforceProfileWizardTypes';
import {flattenWorkforceDepartmentOptions} from '../../utils/WorkforceProfiles/flattenWorkforceDepartmentOptions';

const EMPTY_SELECTION = '';

export interface Props {
  departments: Department[];
  currentLanguage: SupportedLanguages;
  setup: WorkforceProfileWizardSetup;
  lookup: WorkforceProfileResearchLookup;
  departmentTargetProfiles: DepartmentTargetProfileAssignment[];
  isTargetProfilesLoading: boolean;
  isTargetProfilesError: boolean;
  showLookupFields?: boolean;
  onSetupChange: (setup: WorkforceProfileWizardSetup) => void;
  onLookupChange: (lookup: WorkforceProfileResearchLookup) => void;
}

export const WorkforceProfileWizardMetadataFields = ({
  departments,
  currentLanguage,
  setup,
  lookup,
  departmentTargetProfiles,
  isTargetProfilesLoading,
  isTargetProfilesError,
  showLookupFields = false,
  onSetupChange,
  onLookupChange,
}: Props) => {
  const theme = useTheme();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.wizard.setup'});
  const {t: tCreate} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.createModal'});
  const {t: tSeniority} = useTranslation('translation', {keyPrefix: 'shared.seniorityLevels'});
  const {t: tShared} = useTranslation();

  const departmentOptions = flattenWorkforceDepartmentOptions(departments, currentLanguage);

  const targetProfileHelperText = (() => {
    if (!setup.departmentId) return tCreate('selectDepartmentFirst');
    if (isTargetProfilesError) return tShared('shared.fetchingData.error');
    if (!isTargetProfilesLoading && !departmentTargetProfiles.length) return tCreate('noTargetProfilesAvailable');
    return undefined;
  })();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          required
          fullWidth
          size='small'
          label={tCreate('department')}
          value={setup.departmentId}
          onChange={(event) =>
            onSetupChange({
              ...setup,
              departmentId: event.target.value,
              targetProfileId: EMPTY_SELECTION,
            })
          }
          sx={getFieldStyle(theme)}
        >
          <MenuItem value={EMPTY_SELECTION} disabled>
            {tCreate('selectDepartment')}
          </MenuItem>
          {departmentOptions.map((option) => (
            <MenuItem key={option.resourceId} value={option.resourceId}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          required
          fullWidth
          size='small'
          label={tCreate('seniority')}
          value={setup.seniority}
          onChange={(event) => onSetupChange({...setup, seniority: event.target.value})}
          sx={getFieldStyle(theme)}
        >
          <MenuItem value={EMPTY_SELECTION} disabled>
            {tCreate('selectSeniority')}
          </MenuItem>
          {SENIORITY_LEVELS.map((option) => (
            <MenuItem key={option} value={option}>
              {tSeniority(option as SeniorityLevel)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField
          select
          required
          fullWidth
          size='small'
          label={tCreate('targetProfile')}
          value={setup.targetProfileId}
          onChange={(event) => onSetupChange({...setup, targetProfileId: event.target.value})}
          disabled={!setup.departmentId || isTargetProfilesLoading}
          helperText={targetProfileHelperText}
          sx={getFieldStyle(theme)}
        >
          <MenuItem value={EMPTY_SELECTION} disabled>
            {tCreate('selectTargetProfile')}
          </MenuItem>
          {departmentTargetProfiles.map((profile) => (
            <MenuItem key={profile.resourceId} value={profile.resourceId}>
              {profile.title}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {showLookupFields && (
        <>
          <Grid item xs={12}>
            <InfoBanner text={String(t('lookupHint'))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              size='small'
              label={t('personName')}
              value={lookup.personName}
              onChange={(event) => onLookupChange({...lookup, personName: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              size='small'
              label={t('currentEmployer')}
              value={lookup.currentEmployerName}
              onChange={(event) => onLookupChange({...lookup, currentEmployerName: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size='small'
              label={t('currentJobTitle')}
              value={lookup.currentJobTitle}
              onChange={(event) => onLookupChange({...lookup, currentJobTitle: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size='small'
              label={t('profileUrl')}
              value={lookup.profileUrl}
              onChange={(event) => onLookupChange({...lookup, profileUrl: event.target.value})}
              helperText={t('profileUrlHint')}
              sx={getFieldStyle(theme)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size='small'
              label={t('location')}
              value={lookup.location}
              onChange={(event) => onLookupChange({...lookup, location: event.target.value})}
              sx={getFieldStyle(theme)}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
